/**
 * The top bar (the prototype's `topbar()`): the search zone — sidebar toggle (reader) or Back (settings),
 * the global search (`/`), the filter button — and the action zone, which the open screen fills through
 * `<TopbarActions>` (app/topbar-slot.tsx). The status cluster sits at its right end.
 */

import { useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SettingsFilterDialog } from '../settings/shell/types.js';
import { Icon } from '../icons/Icon.js';
import { Tool } from '../ui/Tool.js';
import { useEngine } from '../engine-client/EngineProvider.js';
import { openDialog } from './dialogs.js';
import { StatusCluster } from './StatusCluster.js';
import { DEFAULT_LIST_FILTER, ui, useUi } from './ui-store.js';
import { lastReaderPath } from './navigation.js';

export const PHONE_QUERY = '(max-width: 900px)';

export function isPhone(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(PHONE_QUERY).matches;
}

export interface TopbarProps {
  mode: 'reader' | 'settings';
  /** Settings: the category name for the search placeholder. */
  categoryName?: string;
  /** Receives the action-zone element screens portal into (Workspace provides it as TopbarSlotContext). */
  slotRef(element: HTMLElement | null): void;
  /** Settings: the category's own filter dialog (Sources), instead of "Filter items". */
  filterDialog?: SettingsFilterDialog;
}

const noSubscribe = (): (() => void) => () => undefined;

export function Topbar({ mode, categoryName, slotRef, filterDialog }: TopbarProps) {
  const navigate = useNavigate();
  const engine = useEngine();
  const settings = mode === 'settings';
  const search = useUi((s) => (settings ? s.settingsSearch : s.search));
  const custom = settings ? filterDialog : undefined;
  const customPressed = useSyncExternalStore(custom?.subscribe ?? noSubscribe, () => custom?.pressed() ?? false, () => false);
  const genericPressed = useUi((s) =>
    settings
      ? s.settingsEnabledOnly
      : s.listFilter.unreadOnly || s.listFilter.sourceId !== null || s.listFilter.topicId !== null || s.listFilter.sort !== DEFAULT_LIST_FILTER.sort || s.listFilter.allFolders,
  );
  const filterPressed = custom ? customPressed : genericPressed;

  const toggleSidebar = (): void => {
    if (isPhone()) {
      ui.set((s) => ({ mobileOverlay: s.mobileOverlay === 'navigation' ? 'none' : 'navigation' }));
      return;
    }
    const collapsed = !ui.get().sidebarCollapsed;
    ui.set({ sidebarCollapsed: collapsed });
    void engine.settings.getLocal().then((local) => engine.settings.setLocal('ui', { ...local.ui, sidebarCollapsed: collapsed })).catch(() => undefined);
  };

  return (
    <header className="topbar">
      <div className="search-zone">
        {settings ? (
          <Tool icon="arrow-left" label="Back to reader" onClick={() => navigate(lastReaderPath())} />
        ) : (
          <Tool icon="menu" label="Toggle sidebar" onClick={toggleSidebar} />
        )}
        <label className="global-search">
          <Icon name="search" />
          <input
            id="global-search"
            type="search"
            placeholder={settings ? `Search ${(categoryName ?? 'settings').toLowerCase()}` : 'Search messages'}
            aria-label={settings ? 'Search settings items' : 'Search messages'}
            value={search}
            onChange={(e) => {
              if (settings) ui.set({ settingsSearch: e.target.value });
              else ui.set({ search: e.target.value, mobileOverlay: 'none' });
            }}
          />
          <kbd>/</kbd>
        </label>
        <Tool
          icon="list-filter"
          label={settings ? 'Filter settings items' : 'Filter messages'}
          pressed={filterPressed}
          onClick={() => openDialog(custom ? custom.kind : settings ? 'settings-filter' : 'list-filter', {})}
        />
      </div>
      <div className="action-zone">
        <span className="action-slot" ref={slotRef} />
        <StatusCluster />
      </div>
    </header>
  );
}
